import React from "react";
import {
    VStack,
    Heading,
    Text,
    Box,
    Container,
    Icon,
    Flex,
    Divider,
    Badge,
    Link,
    useColorModeValue,
    UnorderedList,
    OrderedList,
    ListItem,
    Button,
} from "@chakra-ui/react";
import { Helmet } from "react-helmet";
import { useNavigate } from "react-router-dom";
import {
    FaWallet,
    FaBan,
    FaCheckCircle,
    FaTimesCircle,
    FaEnvelope,
    FaHandshake,
    FaClock,
    FaExchangeAlt,
    FaExclamationTriangle,
    FaShieldAlt,
    FaArrowLeft,
} from "react-icons/fa";
import { MdUpdate, MdPolicy } from "react-icons/md";

const Section = ({ icon, title, children, accentColor, cardBg, textColor, borderColor }) => (
    <Box
        w="100%"
        bg={cardBg}
        border="1px solid"
        borderColor={borderColor}
        rounded="xl"
        p={6}
        shadow="sm"
        transition="all 0.2s"
        _hover={{ shadow: "md" }}
    >
        <Flex align="center" mb={4} gap={3}>
            <Flex
                align="center"
                justify="center"
                bg={useColorModeValue("brand.50", "brand.900")}
                p={2}
                rounded="lg"
                color={accentColor}
                flexShrink={0}
            >
                <Icon as={icon} boxSize={5} />
            </Flex>
            <Heading fontSize={{ base: "md", md: "lg" }} fontWeight="700" color={textColor}>
                {title}
            </Heading>
        </Flex>
        <Box color={useColorModeValue("gray.600", "gray.400")} fontSize="md" lineHeight="tall">
            {children}
        </Box>
    </Box>
);

const RefundPolicy = () => {
    const navigate = useNavigate();
    const bg = useColorModeValue("gray.50", "gray.900");
    const cardBg = useColorModeValue("white", "gray.800");
    const textColor = useColorModeValue("gray.800", "white");
    const subTextColor = useColorModeValue("gray.600", "gray.400");
    const accentColor = useColorModeValue("brand.500", "brand.200");
    const borderColor = useColorModeValue("gray.200", "gray.700");
    const warningBg = useColorModeValue("orange.50", "orange.900");
    const warningBorder = useColorModeValue("orange.200", "orange.700");
    const warningText = useColorModeValue("orange.700", "orange.200");
    const dangerBg = useColorModeValue("red.50", "red.900");
    const dangerBorder = useColorModeValue("red.200", "red.700");
    const dangerText = useColorModeValue("red.700", "red.200");
    const successBg = useColorModeValue("green.50", "green.900");
    const successBorder = useColorModeValue("green.200", "green.700");
    const successText = useColorModeValue("green.700", "green.200");

    const sectionProps = { accentColor, cardBg, textColor, borderColor };

    return (
        <>
            <Helmet>
                <title>Refund Policy | Techy India</title>
                <meta
                    name="description"
                    content="Read the Techyindia Refund Policy to understand how refunds are handled for payments made on our platform."
                />
            </Helmet>

            <VStack
                w="100%"
                minH="100vh"
                bg={bg}
                spacing={0}
                align="stretch"
                py={8}
            >
                <Container maxW="container.lg">
                    <VStack spacing={8} align="center" w="100%">

                        {/* Back Button */}
                        <Flex w="100%" justify="flex-start">
                            <Button
                                leftIcon={<Icon as={FaArrowLeft} />}
                                onClick={() => navigate(-1)}
                                variant="ghost"
                                size="sm"
                                color={accentColor}
                                _hover={{ bg: useColorModeValue("brand.50", "brand.900") }}
                            >
                                Go Back
                            </Button>
                        </Flex>
                        {/* Header */}
                        <VStack spacing={4} textAlign="center">
                            <Flex
                                align="center"
                                justify="center"
                                bg={useColorModeValue("brand.100", "brand.900")}
                                p={4}
                                rounded="full"
                                color="brand.500"
                            >
                                <Icon as={MdPolicy} boxSize={7} />
                            </Flex>
                            <Heading
                                fontSize={{ base: "2xl", md: "4xl" }}
                                fontWeight="800"
                                color={textColor}
                                letterSpacing="-0.5px"
                            >
                                Refund{" "}
                                <Text as="span" color="brand.500">
                                    Policy
                                </Text>
                            </Heading>
                            <Text color={subTextColor} fontSize="md" maxW="600px">
                                This Refund Policy explains how refunds are handled for payments
                                made to Techyindia. Please read it carefully before making any payment.
                            </Text>
                            <Badge colorScheme="brand" fontSize="sm" px={3} py={1} rounded="full">
                                Last Updated: March 15, 2026
                            </Badge>
                        </VStack>

                        <Divider borderColor={borderColor} />

                        {/* Section 1: General Policy */}
                        <Section icon={FaWallet} title="1. General Policy" {...sectionProps}>
                            <Text mb={3}>
                                Techyindia operates on a{" "}
                                <Text as="span" fontWeight="700" color={textColor}>
                                    prepaid wallet system
                                </Text>
                                . All payments made by users are added as wallet balance after
                                verification.
                            </Text>
                            <Text mb={3} fontWeight="600" color={accentColor}>
                                All payments are processed outside Google Play and are subject to our verification process.
                            </Text>
                            <Box
                                bg={warningBg}
                                border="1px solid"
                                borderColor={warningBorder}
                                rounded="lg"
                                p={3}
                            >
                                <Text color={warningText} fontWeight="600" fontSize="sm">
                                    ⚠️ Once balance is credited, it is considered{" "}
                                    <Text as="span" fontWeight="800">consumed value</Text>{" "}
                                    within the platform.
                                </Text>
                            </Box>
                        </Section>

                        {/* Section 2: No Automatic Refunds */}
                        <Section icon={FaBan} title="2. No Automatic Refunds" {...sectionProps}>
                            <Text mb={2}>
                                We generally{" "}
                                <Text as="span" fontWeight="700" color={textColor}>
                                    do not offer automatic refunds
                                </Text>{" "}
                                for:
                            </Text>
                            <UnorderedList spacing={1} pl={2}>
                                <ListItem>Successfully credited wallet balances</ListItem>
                                <ListItem>Services already used</ListItem>
                                <ListItem>Partial usage of services</ListItem>
                                <ListItem>SMS or number usage once initiated</ListItem>
                            </UnorderedList>
                        </Section>

                        {/* Section 3: Eligible Refund Cases */}
                        <Section icon={FaCheckCircle} title="3. Eligible Refund Cases" {...sectionProps}>
                            <Text mb={2}>
                                Refunds may be considered only in the following situations:
                            </Text>
                            <Box
                                bg={successBg}
                                border="1px solid"
                                borderColor={successBorder}
                                rounded="lg"
                                p={4}
                                mb={3}
                            >
                                <UnorderedList spacing={2} pl={2} color={successText} fontWeight="600">
                                    <ListItem>Duplicate payment made by mistake</ListItem>
                                    <ListItem>Payment completed but balance not credited</ListItem>
                                    <ListItem>Technical error caused by our system</ListItem>
                                </UnorderedList>
                            </Box>
                            <Text fontSize="sm" fontStyle="italic">
                                All refund requests are subject to manual review and verification.
                            </Text>
                        </Section>

                        {/* Section 4: Non-Refundable Situations */}
                        <Section icon={FaTimesCircle} title="4. Non-Refundable Situations" {...sectionProps}>
                            <Text mb={2}>
                                Refunds will{" "}
                                <Text as="span" fontWeight="700" color={textColor}>
                                    not be provided
                                </Text>{" "}
                                for:
                            </Text>
                            <Box
                                bg={dangerBg}
                                border="1px solid"
                                borderColor={dangerBorder}
                                rounded="lg"
                                p={4}
                            >
                                <UnorderedList spacing={2} pl={2} color={dangerText}>
                                    <ListItem>Incorrect TXID submission</ListItem>
                                    <ListItem>Payment sent to wrong QR or account</ListItem>
                                    <ListItem>
                                        Failure of third-party platforms (e.g., SMS not received due
                                        to external service issues)
                                    </ListItem>
                                    <ListItem>User misuse or violation of Terms of Service</ListItem>
                                    <ListItem>Expired or unused numbers</ListItem>
                                </UnorderedList>
                            </Box>
                        </Section>

                        {/* Section 5: Refund Request Process */}
                        <Section icon={FaEnvelope} title="5. Refund Request Process" {...sectionProps}>
                            <Text mb={3}>To request a refund, users must:</Text>
                            <OrderedList spacing={3} pl={2}>
                                <ListItem>
                                    <Text fontWeight="600" color={textColor} mb={1}>
                                        Contact support via email:
                                    </Text>
                                    <Flex align="center" gap={2}>
                                        <Icon as={FaEnvelope} color={accentColor} boxSize={3} />
                                        <Link
                                            href="mailto:support@techyindia.app"
                                            color={accentColor}
                                            fontWeight="600"
                                            _hover={{ textDecoration: "underline" }}
                                        >
                                            support@techyindia.app
                                        </Link>
                                    </Flex>
                                </ListItem>
                                <ListItem>
                                    <Text fontWeight="600" color={textColor} mb={1}>Provide:</Text>
                                    <UnorderedList spacing={1} pl={2}>
                                        <ListItem>Registered account details</ListItem>
                                        <ListItem>Payment proof (screenshot)</ListItem>
                                        <ListItem>Transaction ID (TXID)</ListItem>
                                        <ListItem>Description of the issue</ListItem>
                                    </UnorderedList>
                                </ListItem>
                            </OrderedList>
                        </Section>

                        {/* Section 6: Processing Time */}
                        <Section icon={FaClock} title="6. Processing Time" {...sectionProps}>
                            <UnorderedList spacing={2} pl={2}>
                                <ListItem>
                                    Refund requests are reviewed within{" "}
                                    <Text as="span" fontWeight="700" color={textColor}>
                                        3–7 business days
                                    </Text>
                                </ListItem>
                                <ListItem>
                                    Approved refunds may take additional time depending on the
                                    payment method
                                </ListItem>
                            </UnorderedList>
                        </Section>

                        {/* Section 7: Refund Method */}
                        <Section icon={FaExchangeAlt} title="7. Refund Method" {...sectionProps}>
                            <Text mb={2}>Refunds (if approved) will be processed via:</Text>
                            <UnorderedList spacing={1} pl={2}>
                                <ListItem>Original payment method (if possible)</ListItem>
                                <ListItem>
                                    Or alternative method as decided by Techyindia
                                </ListItem>
                            </UnorderedList>
                        </Section>

                        {/* Section 8: Abuse and Fraud */}
                        <Section icon={FaShieldAlt} title="8. Abuse and Fraud" {...sectionProps}>
                            <Text mb={2}>Techyindia reserves the right to:</Text>
                            <Box
                                bg={dangerBg}
                                border="1px solid"
                                borderColor={dangerBorder}
                                rounded="lg"
                                p={4}
                            >
                                <UnorderedList spacing={2} pl={2} color={dangerText} fontWeight="600">
                                    <ListItem>Reject refund requests suspected of fraud</ListItem>
                                    <ListItem>
                                        Suspend accounts involved in suspicious activity
                                    </ListItem>
                                </UnorderedList>
                            </Box>
                        </Section>

                        {/* Section 9: Policy Updates */}
                        <Section icon={MdUpdate} title="9. Policy Updates" {...sectionProps}>
                            <Text>
                                We may update this Refund Policy at any time. Changes will be
                                reflected on this page with an updated date.
                            </Text>
                        </Section>

                        {/* Section 10: Contact */}
                        <Section icon={FaEnvelope} title="10. Contact Us" {...sectionProps}>
                            <Text mb={2}>For any refund-related queries:</Text>
                            <Flex align="center" gap={2}>
                                <Icon as={FaEnvelope} color={accentColor} />
                                <Link
                                    href="mailto:support@techyindia.app"
                                    color={accentColor}
                                    fontWeight="600"
                                    _hover={{ textDecoration: "underline" }}
                                >
                                    support@techyindia.app
                                </Link>
                            </Flex>
                        </Section>

                        {/* Section 11: Consent */}
                        <Box
                            w="100%"
                            bg={useColorModeValue("brand.50", "brand.900")}
                            border="1px solid"
                            borderColor={useColorModeValue("brand.200", "brand.700")}
                            rounded="xl"
                            p={6}
                            textAlign="center"
                        >
                            <Icon as={FaHandshake} boxSize={6} color={accentColor} mb={3} />
                            <Heading fontSize="md" fontWeight="700" color={textColor} mb={2}>
                                11. Consent
                            </Heading>
                            <Text color={subTextColor} fontSize="md">
                                By using Techyindia and making payments, you agree to this Refund
                                Policy.
                            </Text>
                        </Box>

                    </VStack>
                </Container>
            </VStack>
        </>
    );
};

export default RefundPolicy;
